import { useContext } from 'react'

import {
  Box, Button, ModalContext, SidebarModal, Text,
} from '@domain-group/suit-theme'

import { type HandleCallbackActionParams, toggleArray } from '@domain-group/javascript-utils'

import useAgency, { useAgencyForm } from '@hooks/useAgency'

import ToggleListItem from '@components/ToggleListItem'

const MODAL_KEY = 'ManageAgencyIntegrationPermissionsModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateAgency: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const integrationModules = [
  { key: 'accounting', title: 'Accounting' },
  { key: 'api_keys', title: 'API Keys' },
  { key: 'crm_sync', title: 'CRM Sync' },
  { key: 'portals', title: 'Portals' },
  { key: 'third_party_integration', title: 'Third Party Integration' },
  { key: 'portal_options', title: 'Portal Options' },
]

const ManageAgencyIntegrationPermissionsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateAgency } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] || {}

  const { agency: initAgency } = modalPayload

  const {
    callbacks: {
      updateAgency: updateFn,
    },
    updating,
  } = useAgency(initAgency)

  const agencyFormPayload = useAgencyForm(initAgency)
  const { entityState, setEntityState } = agencyFormPayload
  const { options } = entityState

  const updateAgencyPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Agency Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Integration" titleSecondLine="Settings" />

      <SidebarModal.Content>
        <Text color="bodyFontLightColor" fontSize="small" lineHeight="1.3" marginTop="small">
          Choose which integration modules you would like to restrict the agency from being able to edit.
          Switching a module to Off will make the module read only for the agency users.
        </Text>

        <Box marginTop="large" flexDirection="column">
          {integrationModules.map(module => (
            <ToggleListItem
              onClick={() => setEntityState({
                options: {
                  ...options,
                  disabled_integration_modules: toggleArray(options.disabled_integration_modules, module.key),
                },
              })}
              key={module.key}
              toggleValue={!options.disabled_integration_modules?.includes(module.key)}
              toggleOnText="On"
              toggleOffText="Off"
            >
              <ToggleListItem.Title>{module.title}</ToggleListItem.Title>
            </ToggleListItem>
          ))}
        </Box>

      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          // @ts-expect-error TS2345
          onClick={() => updateAgency(updateAgencyPayload)}
          size="large"
        >
          Save Settings
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageAgencyIntegrationPermissionsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
